import { AlertTriangle } from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { HalfPriceFlowDemo } from "./HalfPriceFlowDemo";

/* Conteúdo das regras da meia-entrada, sem casca. Serve tanto ao modal
   (checkout) quanto à navegação dentro do card de ingresso, onde abrir um
   segundo modal empilharia dois overlays. */

interface Props {
  audience?: "producer" | "buyer";
  /** "wide" põe as regras numa coluna ao lado do storyboard; "narrow" empilha. */
  variant?: "wide" | "narrow";
  className?: string;
}

function Rule({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-[10px] font-semibold tabular-nums text-primary-foreground">
          {n}
        </span>
        <p className="text-xs font-semibold text-foreground truncate">{title}</p>
      </div>
      <p className="text-xs text-muted-foreground leading-relaxed pl-7">{children}</p>
    </div>
  );
}

export function HalfPriceRulesPanel({ audience = "producer", variant = "wide", className }: Props) {
  const { t } = useTranslation('producerEventForm');
  const wide = variant === "wide";

  return (
    <div
      className={cn(
        wide
          ? "max-h-[72vh] overflow-y-auto lg:overflow-hidden lg:grid lg:grid-cols-[minmax(0,1fr)_324px] lg:grid-rows-[minmax(0,1fr)]"
          : "flex flex-col",
        className
      )}
    >
      {/* storyboard animado */}
      <div className={cn("p-4", wide ? "lg:p-5 lg:flex lg:items-center lg:min-h-0" : "pb-0")}>
        <HalfPriceFlowDemo />
      </div>

      {/* as regras em texto */}
      <div
        className={cn(
          wide
            ? "border-t lg:border-t-0 lg:border-l border-border/50 bg-secondary/15 lg:min-h-0 lg:overflow-y-auto"
            : ""
        )}
      >
        {/* Sem divisores entre as regras: cada um custa 13px de altura e o
            storyboard ao lado é quem deve definir o tamanho do modal. */}
        <div className="p-4 space-y-3.5">
          <Rule n={1} title={t('halfPrice.rules.lot.title')}>
            <Trans
              t={t}
              i18nKey="halfPrice.rules.lot.description"
              components={{ highlight: <span className="text-foreground" /> }}
            />
          </Rule>

          <Rule n={2} title={t('halfPrice.rules.buyerUpload.title')}>
            <Trans
              t={t}
              i18nKey="halfPrice.rules.buyerUpload.description"
              components={{ highlight: <span className="text-foreground" /> }}
            />
          </Rule>

          <Rule n={3} title={t('halfPrice.rules.doorCheck.title')}>
            <Trans
              t={t}
              i18nKey="halfPrice.rules.doorCheck.description"
              components={{ highlight: <span className="text-foreground" /> }}
            />
          </Rule>

          <div className="flex items-start gap-2 p-2.5 rounded-xl border border-amber-500/30 bg-amber-500/10">
            <AlertTriangle className="h-3.5 w-3.5 text-amber-600 shrink-0 mt-0.5" />
            <p className="text-[11px] text-amber-700 dark:text-amber-500 leading-relaxed">
              {audience === "buyer"
                ? t('halfPrice.warning.buyer')
                : t('halfPrice.warning.producer')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
